import { API_BASE } from '../lib/config';
/**
 * TrialWarningBanner - V2.5
 * 
 * Shows a warning banner when the organization's trial is expiring soon (≤7 days)
 * or has expired (paused status).
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AlertTriangle, Clock, X, CreditCard } from 'lucide-react';
import { Button } from './ui/button';

const API = API_BASE;

export default function TrialWarningBanner() {
  const navigate = useNavigate();
  const [trialStatus, setTrialStatus] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [loading, setLoading] = useState(true);

  const token = localStorage.getItem('token');

  useEffect(() => {
    if (!token) return;
    
    const fetchTrialStatus = async () => {
      try {
        const res = await axios.get(`${API}/api/organizations/trial-status/current`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setTrialStatus(res.data);
      } catch (error) {
        console.error('Error fetching trial status:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchTrialStatus();
    
    // Check every 5 minutes
    const interval = setInterval(fetchTrialStatus, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [token]);

  // Don't show if loading, dismissed, or no warning
  if (loading || dismissed || !trialStatus?.has_trial_warning) {
    return null;
  }

  const isExpired = trialStatus.warning_type === 'expired';
  const daysRemaining = trialStatus.days_remaining;

  return (
    <div 
      className={`
        flex items-center justify-between px-4 py-2.5 text-sm
        ${isExpired 
          ? 'bg-red-500/20 border-b border-red-500/30' 
          : 'bg-yellow-500/20 border-b border-yellow-500/30'
        }
      `}
      data-testid="trial-warning-banner"
    >
      <div className="flex items-center gap-3">
        {isExpired ? (
          <AlertTriangle className="h-4 w-4 text-[rgb(var(--tn-text-f87171))] flex-shrink-0" />
        ) : (
          <Clock className="h-4 w-4 text-[rgb(var(--tn-text-facc15))] flex-shrink-0" />
        )}
        
        <span className={isExpired ? "text-[rgb(var(--tn-text-fecaca))]" : "text-[rgb(var(--tn-text-fef08a))]"}>
          {isExpired ? (
            <>
              <span className="font-medium">Testperioden har löpt ut.</span>
              {' '}Aktivera för att fortsätta använda Trainnova.
            </>
          ) : (
            <>
              <span className="font-medium">
                {daysRemaining === 0 
                  ? 'Testperioden går ut idag!' 
                  : `${daysRemaining} ${daysRemaining === 1 ? 'dag' : 'dagar'} kvar av testperioden.`
                }
              </span>
              {' '}Aktivera nu för att behålla alla funktioner.
            </>
          )}
        </span>
      </div>
      
      <div className="flex items-center gap-2">
        <Button
          size="sm"
          className={`
            h-7 text-xs font-medium
            ${isExpired 
              ? 'bg-red-500 hover:bg-red-600 text-white' 
              : 'bg-yellow-500 hover:bg-yellow-600 text-black'
            }
          `}
          onClick={() => navigate('/settings/subscription')}
        >
          <CreditCard className="h-3 w-3 mr-1" />
          Aktivera – 500 kr/lag/mån
        </Button>
        
        {!isExpired && (
          <Button
            size="sm"
            variant="ghost"
            className="h-7 w-7 p-0 text-[rgb(var(--tn-text-facc15))] hover:text-[rgb(var(--tn-text-fde047))] hover:bg-yellow-500/10"
            onClick={() => setDismissed(true)}
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  );
}
